import { type LeadStage } from '@app/queries/settings/leadStages';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

interface LeadStagesRecord {
  [id: string]: LeadStage;
}

export interface LeadStagesState {
  original: LeadStagesRecord;
  edited: LeadStagesRecord;
  isEditing: boolean;
}

const initialState: LeadStagesState = {
  original: {},
  edited: {},
  isEditing: false,
};

export const leadStagesSlice = createSlice({
  name: 'leadStages',
  initialState,
  reducers: {
    setLeadStages: (state, action: PayloadAction<LeadStage[]>) => {
      const stagesRecord: LeadStagesRecord = {};
      action.payload.forEach((stage) => {
        stagesRecord[stage.id] = stage;
      });

      state.original = stagesRecord;
      state.edited = stagesRecord;
      state.isEditing = false;
    },

    startEditing: (state) => {
      state.isEditing = true;
    },

    updateLeadStage: (state, action: PayloadAction<LeadStage>) => {
      state.edited[action.payload.id] = action.payload;
    },

    addLeadStage: (state, action: PayloadAction<LeadStage>) => {
      state.edited[action.payload.id] = action.payload;
    },

    cancelEdits: (state) => {
      state.edited = state.original;
      state.isEditing = false;
    },

    toggleStageStatus: (state, action: PayloadAction<{ id: string; status: string }>) => {
      const { id, status } = action.payload;
      if (state.edited[id]) {
        state.edited[id] = {
          ...state.edited[id],
          status,
        };
      }
    },

    toggleStageEngaged: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      if (state.edited[id]) {
        state.edited[id] = {
          ...state.edited[id],
          engaged: !state.edited[id].engaged,
        };
      }
    },

    setDefaultStage: (state, action: PayloadAction<string>) => {
      Object.keys(state.edited).forEach((stageId) => {
        if (state.edited[stageId]) {
          state.edited[stageId] = {
            ...state.edited[stageId],
            isDefault: stageId === action.payload,
          };
        }
      });
    },

    swapSequenceNumber: (state, action: PayloadAction<{ sourceId: string; targetId: string }>) => {
      const { sourceId, targetId } = action.payload;
      const sourceStage = state.edited[sourceId];
      const targetStage = state.edited[targetId];

      if (sourceStage && targetStage) {
        const tempSequence = sourceStage.sequenceNumber;
        state.edited[sourceId] = {
          ...sourceStage,
          sequenceNumber: targetStage.sequenceNumber,
        };
        state.edited[targetId] = {
          ...targetStage,
          sequenceNumber: tempSequence,
        };
      }
    },
  },
});

export const {
  setLeadStages,
  startEditing,
  updateLeadStage,
  addLeadStage,
  cancelEdits,
  toggleStageStatus,
  toggleStageEngaged,
  setDefaultStage,
  swapSequenceNumber,
} = leadStagesSlice.actions;

export default leadStagesSlice.reducer;
